/**
 * Room Recognition Database Layer
 *
 * Handles all API calls for room recognition management.
 * Follows the same pattern as objectsDatabase.js
 */

import { getAuth } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Cloud Function URL for room recognition admin
const ROOM_RECOGNITION_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/roomRecognitionAdmin';

// Operations that require authentication
const WRITE_OPERATIONS = ['add-room', 'update-room', 'delete-room', 'test-recognition'];

/**
 * Sanitize museum/venue name for use in Firestore paths and Cloud Storage
 * Converts "Museum of the Future: Journey to 2071" → "museum_of_the_future_journey_to_2071"
 */
function sanitizeMuseumName(venueName) {
  if (!venueName) return 'default';
  return venueName
    .replace(/[^\w\s-]/g, '')           // Remove special chars (keep alphanumeric, spaces, hyphens)
    .trim()                              // Remove leading/trailing whitespace
    .replace(/\s+/g, '_')               // Replace spaces with underscores
    .replace(/-+/g, '_')                // Replace hyphens with underscores
    .toLowerCase();                      // Convert to lowercase for consistency
}

/**
 * Call the room recognition admin Cloud Function
 */
async function callRoomRecognitionAPI(endpoint, method = 'GET', data = null) {
  const headers = {
    'Content-Type': 'application/json'
  };

  // Add auth token for write operations
  const needsAuth = WRITE_OPERATIONS.some(op => endpoint.includes(op)) || method !== 'GET';

  if (needsAuth) {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Authentication required. Please log in.');
    }
    const token = await user.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${ROOM_RECOGNITION_URL}${endpoint}`;

  const options = {
    method,
    headers
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // Response wasn't JSON, use status-based message
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result;
}

/**
 * Upload room reference images to Firebase Storage
 *
 * @param {string} museumName - The museum/venue name
 * @param {string} venueId - The venue ID
 * @param {string} roomId - The room ID
 * @param {File[]} imageFiles - Array of image files to upload
 * @returns {Promise<string[]>} - Array of download URLs
 */
export async function uploadRoomImages(museumName, venueId, roomId, imageFiles) {
  const storage = getStorage();
  const uploadPromises = [];
  const urls = [];
  const sanitizedMuseumName = sanitizeMuseumName(museumName);

  for (let i = 0; i < imageFiles.length; i++) {
    const file = imageFiles[i];
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const path = `museums/${sanitizedMuseumName}/room_recognition/${venueId}/${roomId}/${timestamp}_${safeName}`;

    const storageRef = ref(storage, path);

    uploadPromises.push(
      uploadBytes(storageRef, file).then(async (snapshot) => {
        const url = await getDownloadURL(snapshot.ref);
        urls.push(url);
        return url;
      })
    );
  }

  await Promise.all(uploadPromises);
  return urls;
}

/**
 * Convert image file to base64
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:image/xxx;base64, prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
}

/**
 * Convert multiple files to base64
 */
export async function filesToBase64(files) {
  const promises = files.map(file => fileToBase64(file));
  return Promise.all(promises);
}

// ============================================================================
// ROOM CRUD OPERATIONS
// ============================================================================

/**
 * Add a new room with reference images
 *
 * @param {Object} roomData - Room data
 * @param {string} roomData.roomId - Unique room identifier
 * @param {string} roomData.venueId - Museum/venue ID (same as guide ID)
 * @param {string} roomData.venueName - Museum/venue name
 * @param {string} roomData.roomName - Human-readable room name
 * @param {string} roomData.audioGuideId - Associated audio guide ID
 * @param {string} roomData.stopId - Associated stop ID (optional)
 * @param {File[]} imageFiles - Reference image files (3-10 recommended)
 * @param {Object} metadata - Additional metadata
 */
export async function addRoom(roomData, imageFiles, metadata = {}) {
  if (!imageFiles || imageFiles.length === 0) {
    throw new Error('At least one reference image is required');
  }

  if (imageFiles.length > 20) {
    throw new Error('Maximum 20 reference images allowed');
  }

  // Convert images to base64
  console.log(`[RoomDB] Converting ${imageFiles.length} images to base64...`);
  const imagesBase64 = await filesToBase64(imageFiles);

  // Call the admin API — backend handles Storage upload and embedding generation
  console.log(`[RoomDB] Registering room with recognition service...`);
  const result = await callRoomRecognitionAPI('/add-room', 'POST', {
    roomId: roomData.roomId,
    venueId: roomData.venueId,
    venueName: roomData.venueName,
    roomName: roomData.roomName,
    audioGuideId: roomData.audioGuideId,
    stopId: roomData.stopId,
    images: imagesBase64,
    metadata
  });

  return result;
}

/**
 * Update a room with additional reference images
 * @param {string} roomId - Room identifier
 * @param {string} venueName - Museum/venue name
 * @param {string} venueId - Venue ID
 * @param {File[]} imageFiles - Image files to add
 */
export async function updateRoomImages(roomId, venueName, venueId, imageFiles) {
  if (!imageFiles || imageFiles.length === 0) {
    throw new Error('At least one image is required');
  }

  // Convert images to base64
  const imagesBase64 = await filesToBase64(imageFiles);

  // Call the admin API — backend handles Storage upload and re-embedding
  const result = await callRoomRecognitionAPI('/update-room', 'POST', {
    roomId,
    venueName,
    venueId,
    images: imagesBase64
  });

  return result;
}

/**
 * Delete a room
 * @param {string} roomId - Room identifier
 * @param {string} venueName - Museum/venue name
 */
export async function deleteRoom(roomId, venueName) {
  return callRoomRecognitionAPI(`/room/${roomId}?venueName=${encodeURIComponent(venueName)}`, 'DELETE');
}

/**
 * Get all rooms for a venue/museum
 * @param {string} venueName - Museum/venue name
 * @param {string} venueId - Venue ID
 */
export async function getVenueRooms(venueName, venueId) {
  console.log('[roomsDB] getVenueRooms called with venueName:', venueName, 'venueId:', venueId, typeof venueId);
  const result = await callRoomRecognitionAPI(`/venue/${venueId}/rooms?venueName=${encodeURIComponent(venueName)}`, 'GET');
  console.log('[roomsDB] getVenueRooms response:', JSON.stringify(result));
  return result.rooms || [];
}

/**
 * List all venues with room counts
 */
export async function listVenues() {
  const result = await callRoomRecognitionAPI('/venues', 'GET');
  return result.venues || [];
}

/**
 * Get system statistics for a museum
 * @param {string} venueName - Museum/venue name
 */
export async function getStats(venueName) {
  return callRoomRecognitionAPI(`/stats?venueName=${encodeURIComponent(venueName)}`, 'GET');
}

/**
 * Test room recognition with an image (admin testing)
 * @param {File} imageFile - Image file to test
 * @param {string} venueName - Museum/venue name (required)
 * @param {string} venueId - Venue ID (optional, for filtering)
 */
export async function testRecognition(imageFile, venueName, venueId = null) {
  const imageBase64 = await fileToBase64(imageFile);

  return callRoomRecognitionAPI('/test-recognition', 'POST', {
    image: imageBase64,
    venueName,
    venueId
  });
}

/**
 * Refresh the server-side embeddings cache
 * @param {string} venueName - Museum/venue name
 */
export async function refreshCache(venueName) {
  return callRoomRecognitionAPI(`/refresh-cache?venueName=${encodeURIComponent(venueName)}`, 'POST');
}

export default {
  addRoom,
  updateRoomImages,
  deleteRoom,
  getVenueRooms,
  listVenues,
  getStats,
  testRecognition,
  refreshCache,
  uploadRoomImages,
  fileToBase64,
  filesToBase64
};
